/**
 * 通用组件共同的使用的基础组件，原先放在 adapter/form 内部，限制了使用范围，这里提取出来，方便其他地方使用
 * 可用于 vben-form、vben-modal、vben-drawer 等组件使用,
 */

import type { Component } from 'vue'
import type { ComponentProps } from 'vue-component-type-helpers'

import type { BaseFormComponentType } from '@vben/common-ui'

import type { DateRangeDisabledDate } from '#/utils'

import { ApiComponent, globalShareState, IconPicker } from '@vben/common-ui'
import { isFunction } from '@vben/utils'

import { notification } from 'ant-design-vue'

import { InputTag, Uploader } from '#/components'
import { $t } from '#/locales'
import { DateRanges, dayjs } from '#/utils'

import { getPopupContainer } from './utils'

const AutoComplete = defineAsyncComponent(
  () => import('ant-design-vue/es/auto-complete'),
)
const Button = defineAsyncComponent(() => import('ant-design-vue/es/button'))
const Checkbox = defineAsyncComponent(
  () => import('ant-design-vue/es/checkbox'),
)
const CheckboxGroup = defineAsyncComponent(() =>
  import('ant-design-vue/es/checkbox').then((res) => res.CheckboxGroup),
)
const DatePicker = defineAsyncComponent(
  () => import('ant-design-vue/es/date-picker'),
)
const Divider = defineAsyncComponent(() => import('ant-design-vue/es/divider'))
const Input = defineAsyncComponent(() => import('ant-design-vue/es/input'))
const InputNumber = defineAsyncComponent(
  () => import('ant-design-vue/es/input-number'),
)
const InputPassword = defineAsyncComponent(() =>
  import('ant-design-vue/es/input').then((res) => res.InputPassword),
)
const Mentions = defineAsyncComponent(
  () => import('ant-design-vue/es/mentions'),
)
const Radio = defineAsyncComponent(() => import('ant-design-vue/es/radio'))
const RadioGroup = defineAsyncComponent(() =>
  import('ant-design-vue/es/radio').then((res) => res.RadioGroup),
)
const DateRangePicker = defineAsyncComponent(() =>
  import('ant-design-vue/es/date-picker').then((res) => res.RangePicker),
)

function isDateRangeDisabledDate(
  value: unknown,
): value is DateRangeDisabledDate {
  return typeof value === 'function'
}

const DateRangePickerAdapter: Component = (props, { attrs, slots }) => {
  const { presetsRespectDisabledDate, ...pickerProps } = props
  const disabledDate = isDateRangeDisabledDate(attrs.disabledDate)
    ? attrs.disabledDate
    : undefined
  const presets = Array.isArray(attrs.presets)
    ? attrs.presets
    : DateRanges.recentDayPresets(
        disabledDate,
        presetsRespectDisabledDate !== false,
      )

  return h(
    DateRangePicker,
    {
      ...pickerProps,
      ...attrs,
      presets,
    },
    slots,
  )
}
const Rate = defineAsyncComponent(() => import('ant-design-vue/es/rate'))
const Select = defineAsyncComponent(() => import('ant-design-vue/es/select'))
const Space = defineAsyncComponent(() => import('ant-design-vue/es/space'))
const Switch = defineAsyncComponent(() => import('ant-design-vue/es/switch'))
const Textarea = defineAsyncComponent(() =>
  import('ant-design-vue/es/input').then((res) => res.Textarea),
)
const TimePicker = defineAsyncComponent(
  () => import('ant-design-vue/es/time-picker'),
)
const TreeSelect = defineAsyncComponent(
  () => import('ant-design-vue/es/tree-select'),
)

const withDefaultPlaceholder = <T extends Component>(
  name: string,
  component: T,
  type: 'input' | 'select' | false,
  componentProps?: ComponentProps<T>,
) => {
  return defineComponent({
    name: `${name}WithDefaultPlaceholder`,
    inheritAttrs: false,
    setup: (props: Recordable, { attrs, expose, slots }) => {
      const placeholder =
        type === false
          ? props?.placeholder
          : props?.placeholder ||
            attrs?.placeholder ||
            $t(`ui.placeholder.${type}`)
      // 透传组件暴露的方法
      const innerRef = ref()
      const publicApi: Recordable = {}
      expose(publicApi)
      const instance = getCurrentInstance()
      instance?.proxy?.$nextTick(() => {
        for (const key in innerRef.value) {
          if (typeof innerRef.value[key] === 'function') {
            publicApi[key] = innerRef.value[key]
          }
        }
      })
      return () =>
        h(
          component,
          { ...componentProps, placeholder, ...props, ...attrs, ref: innerRef },
          slots,
        )
    },
  })
}

// 这里需要自行根据业务组件库进行适配，需要用到的组件都需要在这里类型说明
export type ComponentType =
  | 'ApiSelect'
  | 'ApiTreeSelect'
  | 'AutoComplete'
  | 'Checkbox'
  | 'CheckboxGroup'
  | 'Custom'
  | 'DateFormatter'
  | 'DatePicker'
  | 'DateRangePicker'
  | 'DefaultButton'
  | 'Divider'
  | 'IconPicker'
  | 'Input'
  | 'InputNumber'
  | 'InputPassword'
  | 'InputTag'
  | 'Mentions'
  | 'PrimaryButton'
  | 'Radio'
  | 'RadioGroup'
  | 'Rate'
  | 'Select'
  | 'Space'
  | 'Switch'
  | 'Text'
  | 'Textarea'
  | 'TimePicker'
  | 'TreeSelect'
  | 'Upload'
  | BaseFormComponentType

const components: Partial<Record<ComponentType, Component>> = {
  // 如果你的组件体积比较大，可以使用异步加载
  // Button: () =>
  // import('xxx').then((res) => res.Button),
  ApiSelect: withDefaultPlaceholder('ApiSelect', ApiComponent, 'select', {
    component: Select,
    loadingSlot: 'suffixIcon',
    visibleEvent: 'onDropdownVisibleChange',
    modelPropName: 'value',
  }),
  ApiTreeSelect: withDefaultPlaceholder(
    'ApiTreeSelect',
    ApiComponent,
    'select',
    {
      component: TreeSelect,
      labelField: 'label',
      valueField: 'value',
      childrenField: 'children',
      loadingSlot: 'suffixIcon',
      modelPropName: 'value',
      optionsPropName: 'treeData',
      visibleEvent: 'onVisibleChange',
    },
  ),
  AutoComplete,
  Checkbox,
  CheckboxGroup,
  DatePicker,
  DateRangePicker: DateRangePickerAdapter,
  // 自定义默认按钮
  DefaultButton: (props, { attrs, slots }) => {
    return h(Button, { ...props, ...attrs, type: 'default' }, slots)
  },
  Divider,
  IconPicker: withDefaultPlaceholder('IconPicker', IconPicker, 'select', {
    iconSlot: 'addonAfter',
    inputComponent: Input,
    modelValueProp: 'value',
  }),
  Input: withDefaultPlaceholder('Input', Input, 'input', {
    allowClear: true,
  }),
  InputNumber: withDefaultPlaceholder('InputNumber', InputNumber, 'input'),
  InputPassword: withDefaultPlaceholder(
    'InputPassword',
    InputPassword,
    'input',
  ),
  Mentions: withDefaultPlaceholder('Mentions', Mentions, 'input'),
  // 自定义主要按钮
  PrimaryButton: (props, { attrs, slots }) => {
    return h(Button, { ...props, ...attrs, type: 'primary' }, slots)
  },
  Radio,
  RadioGroup,
  Rate,
  Select: withDefaultPlaceholder('Select', Select, 'select', {
    allowClear: true,
    class: 'w-full',
    getPopupContainer,
    maxTagCount: 'responsive',
    maxTagTextLength: 4,
  }),
  SelectFixed: withDefaultPlaceholder('SelectFixed', Select, 'select', {
    allowClear: true,
    getPopupContainer,
    maxTagCount: 1,
    maxTagTextLength: 4,
  }),
  InputTag,
  Space,
  Switch,
  Textarea: withDefaultPlaceholder('Textarea', Textarea, 'input'),
  TimePicker,
  TreeSelect: withDefaultPlaceholder('TreeSelect', TreeSelect, 'select'),
  Upload: Uploader,
  Custom: (props, { attrs, slots }) => {
    return h('div', { ...props, ...attrs }, slots)
  },
  Text: (props, { attrs, slots }) => {
    const value = isFunction(props.formatter)
      ? props.formatter(props.value)
      : props.value
    return h(
      'div',
      { ...props, ...attrs },
      {
        default: () => value,
        ...slots,
      },
    )
  },
  DateFormatter: (props, { attrs, slots }) => {
    const value = props.value ? dayjs(props.value).formatDate() : ''
    return h(
      'div',
      { ...props, ...attrs },
      {
        default: () => value,
        ...slots,
      },
    )
  },
}

export function registerGlobalComponents() {
  // 将组件注册到全局共享状态中
  globalShareState.setComponents(components)

  // 定义全局共享状态中的消息提示
  globalShareState.defineMessage({
    // 复制成功消息提示
    copyPreferencesSuccess: (title, content) => {
      notification.success({
        description: content,
        message: title,
        placement: 'bottomRight',
      })
    },
  })
}
